import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LessonShell } from "@/components/LessonShell";
import { COURSE_ACCENT, COURSE_LABEL } from "@/data/lessons";
import { SCORES, SCORE_BY_SLUG } from "@/data/scores";
import { ScoreView } from "./ScoreView";
import { useScorePlayback } from "./useScorePlayback";
import { PaidContentGate, useInsideSchool } from "./PaidContentGate";
import { useVideoScoreSync } from "./useVideoScoreSync";
import { SightReadPanel } from "./SightReadPanel";
import { StageMap } from "./StageMap";
import { PieceCover } from "./PieceCover";
import {
  MAJOR_KEYS, MINOR_KEYS, transposeScore, keyChoiceFor, writtenKey, keyLabel,
  ledgerStrain,
} from "./transpose";
import { STAGE_OF, orderedSlugs } from "./curriculum";
import type { Hand, Score } from "./types";

/**
 * One page renders EVERY piece in the course.
 *
 * The rest of this app is one hand-written component per lesson, which is fine
 * for a fixed set of 165 videos but wrong for a book that keeps growing: adding
 * piece 40 must not mean writing a 40th component. So a piece here is data, and
 * this is the only component that reads it.
 *
 * Route: /lesson/sight-reading-<n>, where <n> is the piece's number in the book.
 */

type Mode = "study" | "playalong" | "sightread";

const ACCENT_FALLBACK = { hex: "#6366f1", bgClass: "bg-indigo-500/15", ringClass: "ring-indigo-500/40" };

export default function SightReadingLesson() {
  const { slug } = useParams<{ slug: string }>();

  const score = useMemo<Score | null>(() => {
    if (!slug) return null;
    // Accept both /lesson/sight-reading-3 and /lesson/sight-reading-first-steps.
    const tail = slug.replace(/^sight-reading-/, "");
    const byNumber = /^\d+$/.test(tail)
      ? SCORES.find((s) => s.n === Number(tail))
      : undefined;
    return byNumber ?? SCORE_BY_SLUG[tail] ?? null;
  }, [slug]);

  if (!score) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold text-zinc-100">
          That piece isn't in the reading book yet
        </h1>
        <p className="mt-3 text-zinc-400">
          The course is still being engraved. Every piece that is ready appears on
          the stage map below.
        </p>
        <div className="mt-10 text-left">
          <StageMap accentHex={ACCENT_FALLBACK.hex} />
        </div>
      </div>
    );
  }

  return <PieceView score={score} />;
}

function PieceView({ score: written }: { score: Score }) {
  const accent = COURSE_ACCENT["sight-reading"] ?? ACCENT_FALLBACK;
  const [mode, setMode] = useState<Mode>("study");
  const [hand, setHand] = useState<Hand | "both">("both");
  const [tempoPct, setTempoPct] = useState(100);
  const [loopBars, setLoopBars] = useState<[number, number] | null>(null);
  const [metronome, setMetronome] = useState(false);
  // The key the student has chosen to read in. Defaults to how it is written.
  const [keyName, setKeyName] = useState(() => writtenKey(written).name);

  // Everything below — engraving, playback, the grid — reads THIS score, so a
  // key change moves the notes, the key signature and the sound together.
  const score = useMemo(() => transposeScore(written, keyName), [written, keyName]);
  const transposed = score !== written;

  // A new piece is written in its own key; don't carry the last one's choice.
  useEffect(() => { setKeyName(writtenKey(written).name); }, [written]);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const playback = useScorePlayback(score, {
    bpm: Math.round(score.tempo * (tempoPct / 100)),
    loop: true,
    barRange: loopBars ?? undefined,
    hand,
    metronome,
    countIn: true,
  });

  const videoSync = useVideoScoreSync(videoRef, score);

  const available = useMemo(() => new Set(SCORES.map((s) => s.slug)), []);
  const order = useMemo(() => orderedSlugs(available), [available]);
  const posInCourse = order.indexOf(score.slug);
  const prevSlug = posInCourse > 0 ? order[posInCourse - 1] : null;
  const nextSlug =
    posInCourse >= 0 && posInCourse < order.length - 1 ? order[posInCourse + 1] : null;
  const stage = STAGE_OF[score.slug];

  const playingIndex = mode === "playalong" ? videoSync.playingIndex : playback.playingIndex;

  // The playhead reads the same clock as the sounding note. `playback.beat` is
  // quarter notes from the start of the PLAYED RANGE, so add the range's own
  // offset to get a position in the piece the score can place.
  // A GETTER, not a value: the score reads it once per animation frame and
  // moves the playhead itself. Passing a number meant every frame of playback
  // re-rendered this lesson and the engraved score beneath it.
  const loopOffset = loopBars ? (loopBars[0] - 1) * score.barQuarters : 0;
  const getPlayheadQ = useCallback(() => {
    const b = playback.beatRef.current;
    return b == null ? null : loopOffset + b;
  }, [playback.beatRef, loopOffset]);

  // Licensed songs render only inside the school. See PaidContentGate.
  const insideSchool = useInsideSchool();
  if (score && !score.publicSafe && insideSchool === false) {
    return <PaidContentGate title={score.title} composer={score.composer} />;
  }

  return (
    <LessonShell
      course={COURSE_LABEL["sight-reading"] ?? "Sight Reading"}
      courseSlug="sight-reading"
      lessonOrder={score.n}
      title={score.title}
      description={score.jasonNote}
      videoUrl={score.video ?? ""}
      composer={score.composer}
      accent={accent}
    >
      {/* Align the body with the hero's measure. Without this the page ran
          full-bleed, so on a phone the nav buttons were clipped by both screen
          edges and the reading measure was far too wide on a desktop. */}
      <div className="mx-auto max-w-5xl space-y-8 px-4 pt-8 lg:px-8">
        {/* ---- getting back out ----
             A piece page had no way back to the course and no way to the next
             piece, so the only exit was the browser's back button. In the LMS
             the companion is inside an iframe, where the browser back button
             navigates the PARENT — students were leaving the lesson entirely. ---- */}
        <nav className="flex flex-wrap items-center gap-2 text-sm">
          <a
            href="/course/sight-reading"
            className="rounded-lg px-3 py-2 font-medium text-zinc-200 ring-1 ring-zinc-700 transition hover:bg-zinc-800"
          >
            ← All 30 pieces
          </a>
          {stage && (
            <span className="text-xs uppercase tracking-widest text-zinc-500">
              Stage {stage}
            </span>
          )}
          <span className="flex-1" />
          {prevSlug ? (
            <a
              href={`/lesson/sight-reading-${prevSlug}`}
              className="rounded-lg px-3 py-2 text-zinc-300 ring-1 ring-zinc-700 transition hover:bg-zinc-800"
            >
              ← Previous
            </a>
          ) : (
            <span className="rounded-lg px-3 py-2 text-zinc-600 ring-1 ring-zinc-800">← Previous</span>
          )}
          {nextSlug ? (
            <a
              href={`/lesson/sight-reading-${nextSlug}`}
              className="rounded-lg px-3 py-2 font-medium text-zinc-100 ring-1 transition hover:bg-zinc-800"
              style={{ borderColor: accent.hex }}
            >
              Next piece →
            </a>
          ) : (
            <a
              href="/course/sight-reading"
              className="rounded-lg px-3 py-2 font-medium text-zinc-100 ring-1 ring-zinc-700 transition hover:bg-zinc-800"
            >
              Finish the course →
            </a>
          )}
        </nav>

        {/* ---- the piece's picture from the printed book ----
             11 of the 30 pieces have no illustration. They used to render
             nothing here, which made those pages look unfinished beside the
             19 that do. PieceCover draws the piece instead of inventing a
             photograph for it. ---- */}
        {score.art ? (
          /* The whole plate, never a slice of it — but also never white bands
             around it. Every hero is the same 16:9 frame: a blurred copy of
             the plate fills the frame, and the sharp plate sits whole on top,
             whatever its own shape. */
          <figure className="relative aspect-video overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900">
            <img
              src={score.art}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
              loading="lazy"
            />
            <img
              src={score.art}
              alt={`Illustration for ${score.title} from the Piano Reading Book`}
              className="relative h-full w-full object-contain"
              loading="lazy"
            />
          </figure>
        ) : score.background ? (
          /* The eleven gap-fillers have no book plate, so their generated
             scene stands in as the hero. It is authored at exactly 16:9, so
             it renders whole at its natural aspect — nothing to crop. */
          <figure className="overflow-hidden rounded-xl border border-zinc-800 bg-black">
            <img
              src={score.background}
              alt={`Scene for ${score.title}`}
              className="aspect-video w-full"
              loading="lazy"
            />
          </figure>
        ) : (
          <PieceCover score={score} />
        )}

        {/* ---- what this piece is for ----
             When the piece has a generated background it becomes the backdrop
             for this panel, with a dark scrim so the text keeps its contrast.
             The images are composed with a quiet upper two thirds precisely so
             this works. ---- */}
        {/* Text over a scrimmed photograph is the single loudest "generated
            app" tell, and it costs contrast for nothing. The artwork already
            has its own frame above; this panel is just type on a surface. */}
        <section className="relative overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium uppercase tracking-widest text-zinc-500">
            {stage && <span style={{ color: accent.hex }}>Stage {stage}</span>}
            <span>{keyLabel(score.key)}</span>
            <span>{score.time[0]}/{score.time[1]}</span>
            <span>{score.bars} bars</span>
            <span>{score.staves > 1 ? "Both hands" : "Right hand"}</span>
            {/* Number the piece the way the student reached it. This badge used
                to show the position in the graded curriculum, so lesson 21
                ("Two by Two") announced itself as "Piece 4 of 30" — the page
                header and the badge contradicting each other on screen. */}
            <span>Piece {score.n} of {order.length}</span>
          </div>

          {score.jasonNote && (
            <p className="mt-4 text-[17px] leading-relaxed text-zinc-200">
              {score.jasonNote}
            </p>
          )}

          {score.teaches.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2">
              {score.teaches.map((t) => (
                <li
                  key={t}
                  className={`rounded-full px-3 py-1 text-xs text-zinc-300 ring-1 ${accent.bgClass} ${accent.ringClass}`}
                >
                  {t}
                </li>
              ))}
            </ul>
          )}

          {score.newForThisEdition && (
            <p className="mt-4 border-t border-zinc-800 pt-3 text-sm leading-relaxed text-zinc-400">
              <span className="mr-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                New for this edition
              </span>
              Not in the printed book — written to fill a gap in it.
              {score.fillsGap ? ` ${score.fillsGap}` : ""}
            </p>
          )}

          {score.creditNote && (
            <p className="mt-3 text-sm leading-relaxed text-zinc-500">
              {score.creditNote}
            </p>
          )}
        </section>

        {/* ---- read it in any key, major or minor ----
             Jason's own note on Twinkle asks for exactly this ("try playing
             this melody on various key signatures"). Changing the key moves
             the engraving, the key signature and the playback together.
             The minor row goes further: it lowers the third, sixth and seventh
             so a major tune actually becomes its parallel natural minor,
             rather than just being redrawn under a different signature. ---- */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              Read it in
            </span>
            {transposed ? (
              <button
                onClick={() => setKeyName(writtenKey(written).name)}
                className="text-[11px] font-medium text-zinc-400 underline underline-offset-2 hover:text-zinc-200"
              >
                back to {writtenKey(written).name} (as written)
              </button>
            ) : (
              <span className="text-[11px] text-zinc-500">as written</span>
            )}
          </div>

          {([["Major", MAJOR_KEYS], ["Natural minor", MINOR_KEYS]] as const).map(
            ([label, keys]) => (
              <div key={label} className="mb-2 last:mb-0">
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-600">
                  {label}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {keys.map((k) => {
                    const chosen = keyChoiceFor(keyName);
                    const home = writtenKey(written);
                    const on = chosen.pc === k.pc && chosen.mode === k.mode;
                    const isHome = home.pc === k.pc && home.mode === k.mode;
                    return (
                      <button
                        key={k.name}
                        onClick={() => setKeyName(k.name)}
                        title={isHome ? `${k.name} — as written` : k.name}
                        className="h-9 min-w-11 rounded-md border px-2 text-[13px] font-semibold transition"
                        style={{
                          borderColor: on ? accent.hex : isHome ? "#52525b" : "#3f3f46",
                          background: on ? accent.hex : "transparent",
                          color: on ? "#0b0b0f" : "#d4d4d8",
                        }}
                      >
                        {k.tonic}
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
          )}

          {transposed && (
            <p className="mt-3 text-[12px] leading-relaxed text-zinc-500">
              {keyChoiceFor(keyName).mode === "minor" && writtenKey(written).mode === "major"
                ? "Same tune, minor mode — the third, sixth and seventh have dropped a semitone. Listen to what that does to the colour, then read it."
                : "Same tune, new key signature — the note names change, the shape does not. Sing the numbers, not the letters."}
            </p>
          )}
          {/* Say it out loud when a key has pushed the music off the stave. The
              piece is still correct and still playable; it is just no longer the
              easiest READ, and on a sight-reading page that is worth a sentence
              rather than a surprise mid-bar. */}
          {ledgerStrain(score) > Math.max(2, ledgerStrain(written)) && (
            <p className="mt-2 text-[12px] leading-relaxed text-amber-500/80">
              Heads up: in this key the music reaches {ledgerStrain(score)} ledger
              lines off the stave. Playable, but not the easiest read — try a key
              a step or two away instead.
            </p>
          )}
        </div>


        {/* ---- mode switch ----
             Play-along needs a lesson video aligned to the score, which no
             piece has yet, so it used to render as a permanently greyed-out
             tab on all 30 pieces — indistinguishable from a broken control.
             Offer it only where it can actually do something. ---- */}
        <div className="flex flex-wrap gap-2">
          {([
            ["study", "Study"],
            ...(score.midiSync ? [["playalong", "Play-along"]] : []),
            ["sightread", "Sight-read"],
          ] as [Mode, string][]).map(([m, label]) => {
            const on = mode === m;
            const disabled = false;
            return (
              <button
                key={m}
                disabled={disabled}
                onClick={() => setMode(m)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                  on ? "text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"
                } ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
                style={on ? { background: accent.hex } : { border: "1px solid #3f3f46" }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* ---- study / play-along ---- */}
        {mode !== "sightread" && (
          <section className="space-y-4">
            {mode === "playalong" && score.video && (
              <div className="overflow-hidden rounded-xl border border-zinc-800 bg-black">
                <video
                  ref={videoRef}
                  src={score.video}
                  controls
                  playsInline
                  className="aspect-video w-full"
                />
              </div>
            )}

            <div className="rounded-xl border border-zinc-800 bg-[#F6F6FA] p-3">
              <ScoreView
                score={score}
                playingIndex={playingIndex}
                focusHand={hand}
                barRange={loopBars ?? undefined}
                showLyrics
                getPlayheadQ={getPlayheadQ}
              />
            </div>

            {mode === "study" ? (
              <Transport
                score={score}
                accentHex={accent.hex}
                playing={playback.playing}
                loading={playback.loading}
                onPlay={playback.play}
                onStop={playback.stop}
                tempoPct={tempoPct}
                setTempoPct={setTempoPct}
                hand={hand}
                setHand={setHand}
                loopBars={loopBars}
                setLoopBars={setLoopBars}
                metronome={metronome}
                setMetronome={setMetronome}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-4 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
                <p className="text-sm text-zinc-300">
                  The score follows Jason's hands in the video. If it drifts, nudge
                  it.
                </p>
                <label className="flex items-center gap-2 text-sm text-zinc-400">
                  <span className="font-mono text-xs">nudge</span>
                  <input
                    type="range" min={-1} max={1} step={0.02}
                    value={videoSync.nudge}
                    onChange={(e) => videoSync.setNudge(Number(e.target.value))}
                    className="w-40"
                  />
                  <span className="w-14 text-right font-mono text-xs tabular-nums">
                    {videoSync.nudge >= 0 ? "+" : ""}{videoSync.nudge.toFixed(2)}s
                  </span>
                </label>
              </div>
            )}
          </section>
        )}

        {/* ---- sight-read ---- */}
        {mode === "sightread" && (
          <SightReadPanel score={score} accentHex={accent.hex} />
        )}

        {/* ---- where this sits ---- */}
        <StageMap accentHex={accent.hex} current={score.slug} />
      </div>
    </LessonShell>
  );
}

interface TransportProps {
  score: Score;
  accentHex: string;
  playing: boolean;
  loading: boolean;
  onPlay: () => void;
  onStop: () => void;
  tempoPct: number;
  setTempoPct: (v: number) => void;
  hand: Hand | "both";
  setHand: (h: Hand | "both") => void;
  loopBars: [number, number] | null;
  setLoopBars: (v: [number, number] | null) => void;
  metronome: boolean;
  setMetronome: (v: boolean) => void;
}

/**
 * The controls a student reaches for with their hands already on the keys, so
 * every target is large and the labels say what will happen.
 */
function Transport(p: TransportProps) {
  const bpm = Math.round(p.score.tempo * (p.tempoPct / 100));
  const grand = p.score.staves > 1;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-4 rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
      <button
        onClick={p.playing ? p.onStop : p.onPlay}
        disabled={p.loading}
        className="min-w-[112px] rounded-full px-6 py-3 text-sm font-semibold text-zinc-950 transition hover:brightness-110 disabled:opacity-50"
        style={{ background: p.accentHex }}
      >
        {p.loading ? "Loading…" : p.playing ? "Stop" : "Play"}
      </button>

      <label className="flex items-center gap-3 text-sm text-zinc-300">
        <span className="w-14 font-mono text-xs uppercase tracking-widest text-zinc-500">
          tempo
        </span>
        <input
          type="range" min={40} max={120} step={5}
          value={p.tempoPct}
          onChange={(e) => p.setTempoPct(Number(e.target.value))}
          className="w-36"
        />
        <span className="w-20 font-mono text-xs tabular-nums">
          {bpm} bpm
        </span>
      </label>

      {grand && (
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            hands
          </span>
          {([["both", "Both"], ["rh", "Right"], ["lh", "Left"]] as [Hand | "both", string][])
            .map(([h, label]) => (
              <button
                key={h}
                onClick={() => p.setHand(h)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  p.hand === h
                    ? "bg-zinc-100 text-zinc-900"
                    : "border border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                }`}
              >
                {label}
              </button>
            ))}
        </div>
      )}

      <label className="flex min-h-[40px] cursor-pointer items-center gap-2 px-1 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={p.metronome}
          onChange={(e) => p.setMetronome(e.target.checked)}
          className="h-4 w-4 accent-indigo-500"
        />
        Click
      </label>

      <LoopPicker
        bars={p.score.bars}
        value={p.loopBars}
        onChange={p.setLoopBars}
      />
    </div>
  );
}

function LoopPicker({
  bars, value, onChange,
}: {
  bars: number;
  value: [number, number] | null;
  onChange: (v: [number, number] | null) => void;
}) {
  const windows: [number, number][] = [];
  for (let start = 1; start <= bars; start += 4) {
    windows.push([start, Math.min(bars, start + 3)]);
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
        loop
      </span>
      <button
        onClick={() => onChange(null)}
        className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
          !value ? "bg-zinc-100 text-zinc-900"
                 : "border border-zinc-600 text-zinc-300 hover:bg-zinc-800"
        }`}
      >
        Whole piece
      </button>
      {windows.map(([a, b]) => {
        const on = value?.[0] === a && value?.[1] === b;
        return (
          <button
            key={`${a}-${b}`}
            onClick={() => onChange([a, b])}
            className={`rounded-full px-3 py-1.5 text-xs font-medium tabular-nums transition ${
              on ? "bg-zinc-100 text-zinc-900"
                 : "border border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            }`}
          >
            {a}–{b}
          </button>
        );
      })}
    </div>
  );
}
